/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-report-template-grid/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import cn from 'clsx';
import { OpraReportTemplateFields } from '@components/admin/admin-report-template-grid/admin-report-template-grid.interface';
import EditTemplateOrganizationSetup from '@components/admin/edit-template-organization-setup';
import ActionDialog from '@components/core/action-dialog';
import Loading from '@components/core/loading';
import Error from '@components/error/error';
import AddReportTemplateDialog from '../add-report-template-dialog';
import { OpraReportTemplate } from '@interfaces/report-templates.interface';
import { ReportStatusUpdatedFields } from '@interfaces-clean/reports.interface';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useAuthorization, useBoolean, useTranslation } from 'hooks/index';
import { stringDateWithoutTimeFromUtc } from 'utils/convertor';
import { REPORT_TEMPLATE_COLUMNS } from 'views/components/admin/admin-report-template-grid/admin-report-template-grid.data';
import ExpandableGridV2 from 'views/components/core/expandable/grid-v2';
import {
    ExcelExportConfig,
    IActionItemV2,
} from 'views/components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { ExtraMenuItem, ExtraMenuItemPosition } from 'views/components/core/grid-header/grid-menu/grid-menu.interface';
import Constants from '@utils/constants';
import NotificationService from '@utils/notification';
import { addPrefixedRecordIdsAndTemplateNamesToReportTemplates } from '@utils/prefixes-helper';
import { connectToReportingServer, reportingConnection } from '@utils/reporting-signalr-service';
import { isReportStatusUpdated } from '@utils/shared';
import { AUDIT_RECORD_ID_PREFIXES } from '@queries/audit';
import { auditRecordIdPrefixes } from '@queries/generated/audit.generated';
import { useGetOpraReportTemplates, useGetPowerBIReportTemplates } from '@apis/bff-api/report-templates';
import './styles.scss';

const AdminReportTemplateGrid = () => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const canEdit = !!user?.canWriteSystemAdminLevel?.();
    const openReportTemplateDialog = useBoolean(false);
    const openEditOrgTemplateDialog = useBoolean(false);
    const openConfirmRetireDialog = useBoolean(false);
    const openConfirmPublishDialog = useBoolean(false);
    const [submitReport, setSubmitReport] = useState<() => () => void>();
    const [reportTemplatesReadyItems, setReportTemplatesReadyItems] = useState<OpraReportTemplate[]>([]);
    const [currentReportTemplate, setCurrentReportTemplate] = useState<OpraReportTemplate | null>(null);
    const {
        data: reportTemplatesData,
        mutate: mutateOpraReportTemplates,
        isLoading,
        error,
    } = useGetOpraReportTemplates();
    const {
        data: powerBIReportTemplatesData,
        mutate: mutatePBIReportTemplates,
        isLoading: isLoadingPowerBIReportTemplates,
    } = useGetPowerBIReportTemplates(Constants.ObjectId.Empty); // On System Admin Level should be default id; On Organization Level should be organizationKey variable
    const { data: prefixesData, loading: isLoadingPrefixes } = useQuery<auditRecordIdPrefixes>(
        AUDIT_RECORD_ID_PREFIXES,
        { fetchPolicy: 'cache-first' },
    );

    useEffect(() => {
        connectToReportingServer(user);
    }, []);

    const getImprovedTemplateArray = (templates: OpraReportTemplateFields[]) =>
        templates?.map((item: OpraReportTemplateFields) => {
            const result = powerBIReportTemplatesData?.find((pbiItem) => pbiItem.id === item.pbiReportId);

            return {
                ...item,
                _powerBITemplateName: result?.name,
                reportStatusName: t(`admin:${item.reportStatusName}`),
            };
        });

    const onClose = (shouldUpdate: boolean | null | undefined, keepCurrentTemplate?: boolean) => {
        if (openReportTemplateDialog.value) openReportTemplateDialog.setFalse();
        if (shouldUpdate) {
            mutatePBIReportTemplates().catch((err) => window.console.error(err));
            mutateOpraReportTemplates().catch((err) => window.console.error(err));
        }
        if (currentReportTemplate && !keepCurrentTemplate) {
            setCurrentReportTemplate(null);
        }
    };

    const sortTemplatesByName = (templates?: OpraReportTemplate[]) => {
        if (!templates) return [];
        templates = restructuredUpdatedCreatedBy(templates) as OpraReportTemplateFields[];
        return orderBy(getImprovedTemplateArray(templates), [{ field: 'templateName', dir: 'asc' }]);
    };

    useEffect(() => {
        const reportTemplatesList = addPrefixedRecordIdsAndTemplateNamesToReportTemplates(
            sortTemplatesByName(reportTemplatesData),
            prefixesData?.auditRecordIdPrefixes,
        );
        setReportTemplatesReadyItems(reportTemplatesList);
    }, [reportTemplatesData, powerBIReportTemplatesData]);

    const reportMessageForUserHandler = (message: string) => {
        NotificationService.showNotification(t(`reports:${message}`));
    };

    const generationUpdateForUserHandler = (data: ReportStatusUpdatedFields) => {
        if (data && isReportStatusUpdated(reportTemplatesReadyItems, data)) {
            mutateOpraReportTemplates().catch((err) => window.console.error(err));
        }
    };

    useEffect(() => {
        reportingConnection.on('ReportMessageForUser', reportMessageForUserHandler);
        reportingConnection.on('ReportUpdatedForUser', generationUpdateForUserHandler);

        return () => {
            reportingConnection.off('ReportMessageForUser', reportMessageForUserHandler);
            reportingConnection.off('ReportUpdatedForUser', generationUpdateForUserHandler);
        };
    }, [reportTemplatesReadyItems]);

    const extraMenuItems: ExtraMenuItem[] = [
        {
            position: ExtraMenuItemPosition.right,
            tooltipTitle: t('admin:addTemplate'),
            onClick: () => openReportTemplateDialog.setTrue(),
            customClassName: 'grid-header__item--always-visible',
            icon: ['fal', 'plus'],
            disabled: !canEdit,
            testId: 'add-template',
        },
    ];

    const actions = (template: OpraReportTemplate): IActionItemV2[] => [
        {
            label: 'admin:editTemplate',
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentReportTemplate(template);
                openReportTemplateDialog.setTrue();
            },
            enabled:
                canEdit &&
                template.status !== Constants.Reporting.ReportTemplateStatus.retired &&
                template.status !== Constants.Reporting.ReportTemplateStatus.publishing,
            testId: 'edit-template',
        },
        {
            label: 'admin:editOrgSetup',
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentReportTemplate(template);
                openEditOrgTemplateDialog.setTrue();
            },
            enabled: canEdit && template.status === Constants.Reporting.ReportTemplateStatus.published,
            testId: 'edit-org-setup',
        },
    ];

    const exportFileName = `OPRA Report Templates List_${stringDateWithoutTimeFromUtc(new Date(), 'DD-MMM-YYYY')}`;

    const exportConfig: ExcelExportConfig = {
        fileName: exportFileName,
        useColumn: true,
        useData: true,
    };

    if (isLoading || isLoadingPowerBIReportTemplates || isLoadingPrefixes) return <Loading />;
    if (error) return <Error error={error} />;

    return (
        <>
            {openReportTemplateDialog.value && (
                <AddReportTemplateDialog
                    onClose={onClose}
                    confirmActionFn={(submitFn, actionType) => {
                        if (actionType === Constants.Reporting.reportingDialogConfirmation.retire)
                            openConfirmRetireDialog.setTrue();

                        if (actionType === Constants.Reporting.reportingDialogConfirmation.publish)
                            openConfirmPublishDialog.setTrue();

                        setSubmitReport(() => () => submitFn());
                    }}
                    currentReportTemplate={currentReportTemplate}
                    powerBIReportTemplatesData={powerBIReportTemplatesData || []}
                    className={cn({ 'k-d-none': openConfirmPublishDialog.value })}
                />
            )}
            {(openConfirmRetireDialog.value || openConfirmPublishDialog.value) && (
                <ActionDialog
                    title={t(
                        openConfirmRetireDialog.value ? 'reports:retireDialogTitle' : 'reports:publishDialogTitle',
                    )}
                    onClose={() => {
                        if (openConfirmRetireDialog.value) openConfirmRetireDialog.setFalse();
                        if (openConfirmPublishDialog.value) openConfirmPublishDialog.setFalse();
                        openReportTemplateDialog.setTrue();
                    }}
                    cancelText={t('common:cancel')}
                    submitText={t('common:continue')}
                    onSubmit={() => {
                        if (submitReport) submitReport();
                        if (openConfirmRetireDialog.value) openConfirmRetireDialog.setFalse();
                        if (openConfirmPublishDialog.value) openConfirmPublishDialog.setFalse();
                    }}
                >
                    <>
                        <p>
                            {t(
                                openConfirmRetireDialog.value
                                    ? 'reports:retireDialogMainText'
                                    : 'reports:publishDialogMainText',
                            )}
                        </p>
                        {openConfirmRetireDialog.value && (
                            <ul className="k-mb-5">
                                <li>{t('reports:retireDialogPoint1')}</li>
                                <li>{t('reports:retireDialogPoint2')}</li>
                                <li>{t('reports:retireDialogPoint3')}</li>
                            </ul>
                        )}
                        <p>{t('reports:wouldYouLikeContinue')}</p>
                    </>
                </ActionDialog>
            )}
            {openEditOrgTemplateDialog.value && (
                <EditTemplateOrganizationSetup
                    open={openEditOrgTemplateDialog.value}
                    onClose={() => {
                        openEditOrgTemplateDialog.setFalse();
                        if (currentReportTemplate) setCurrentReportTemplate(null);
                    }}
                    currentReportTemplate={currentReportTemplate}
                />
            )}
            <ExpandableGridV2
                title={t('admin:templates')}
                className="report-templates-grid"
                hideCheckbox
                hideExpand
                customKey="report-template"
                key="report-template"
                id="report-template"
                items={reportTemplatesReadyItems}
                rowActions={actions}
                scrollable="scrollable"
                groupable
                reorderable
                resizable
                fixedScroll
                pageable={false}
                columns={REPORT_TEMPLATE_COLUMNS(t)}
                extraMenuItems={extraMenuItems}
                exportConfig={exportConfig}
            />
        </>
    );
};

export default AdminReportTemplateGrid;
